import { useState, useRef } from 'react';
import QRCode from 'react-qr-code';
import { callMfaSetup } from '../../api/mfaApi.js';

/**
 * MfaSetupModal
 *
 * Multi-step MFA enrolment wizard — used in two contexts:
 *   1. `mfa-setup-required` (new user / grace-exhausted): must complete before
 *      access is granted.
 *   2. Grace-period notice "Set up now": user chose to enrol immediately.
 *
 * Steps:
 *   choose       — pick WebAuthn passkey or TOTP authenticator app
 *   webauthn     — browser passkey/biometric registration (auto-starts)
 *   totp         — scan QR code + enter confirmation code
 *
 * Props:
 *   challengeToken  mfa-challenge JWT from tenant-login
 *   onComplete      (token, user) → void — called on successful enrolment;
 *                   the parent calls completeLogin() to establish the session
 *   onBack          () → void — go back (cancel setup)
 *   functionsBaseUrl  string? — env-specific Function App base URL (test FA for
 *                     a beta tenant during central login); omit for current env
 *   callSetup       (action, body) → Promise? — replaces the mfa-setup call; the
 *                   super-admin step-up dialog routes to superadmin-mfa-setup
 *                   (FDNA-02b). Its completion carries no token.
 *
 * @param {{
 *   challengeToken: any,
 *   onComplete: any,
 *   onBack: any,
 *   functionsBaseUrl?: any,
 *   callSetup?: (action: string, body: object) => Promise<any>,
 * }} props
 */
function MfaSetupModal({ challengeToken, onComplete, onBack, functionsBaseUrl, callSetup }) {
  const setup = (action, body) =>
    callSetup ? callSetup(action, body) : callMfaSetup(action, body, functionsBaseUrl);
  const [step, setStep] = useState('choose');
  const [setupToken, setSetupToken] = useState(null); // TOTP: from totp-generate
  const [otpauthUri, setOtpauthUri] = useState(null); // TOTP: QR code URI
  const [totpLabel, setTotpLabel] = useState(null); // TOTP: entry name in the app
  const [totpTag, setTotpTag] = useState(null); // TOTP: short unique tag
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const webAuthnStarted = useRef(false);

  // ── Shared styles ─────────────────────────────────────────────────────────

  const monoLabel = {
    display: 'block',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 6,
  };

  const backBtn = {
    width: '100%',
    marginTop: 10,
    padding: '8px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    textDecoration: 'underline',
    textDecorationColor: 'var(--border)',
  };

  // ── Error display ─────────────────────────────────────────────────────────

  function ErrorBanner({ msg }) {
    if (!msg) return null;
    return (
      <div
        style={{
          padding: '8px 12px',
          borderRadius: 8,
          background: 'var(--danger-bg)',
          border: '1px solid rgba(251,113,133,0.25)',
          color: 'var(--danger)',
          fontSize: 12,
          marginBottom: 14,
        }}
      >
        {msg}
      </div>
    );
  }

  // ── WebAuthn registration ─────────────────────────────────────────────────

  async function startWebAuthnRegistration() {
    if (webAuthnStarted.current) return;
    webAuthnStarted.current = true;
    setStep('webauthn');
    setLoading(true);
    setError('');

    try {
      // 1. Get registration options from backend
      const optionsResult = await setup('webauthn-options', { challengeToken });
      if (optionsResult.error) {
        setError(optionsResult.error);
        setLoading(false);
        webAuthnStarted.current = false;
        return;
      }

      // 2. Trigger browser's passkey/biometric registration dialog
      const { startRegistration } = await import('@simplewebauthn/browser');
      const registrationResponse = await startRegistration({
        optionsJSON: optionsResult.registrationOptions,
      });

      // 3. Verify the registration with the backend
      const verifyResult = await setup('webauthn-verify', {
        setupToken: optionsResult.setupToken,
        registrationResponse,
        origin: window.location.origin,
      });

      if (verifyResult.error) {
        setError(verifyResult.error);
        setLoading(false);
        webAuthnStarted.current = false;
        return;
      }

      onComplete(verifyResult.token, verifyResult.user);
    } catch (e) {
      if (e?.name === 'NotAllowedError') {
        setError('Registration was cancelled. Click "Try again" to retry.');
      } else if (e?.name === 'InvalidStateError') {
        setError(
          'A passkey is already registered on this device. Try a different device or use an authenticator app instead.'
        );
      } else if (e?.name === 'SecurityError') {
        setError('Security error — ensure you are on the correct domain and try again.');
      } else {
        setError(
          'Passkey registration failed. Please try again or use an authenticator app instead.'
        );
        console.error('[MfaSetupModal] WebAuthn registration error:', e);
      }
      setLoading(false);
      webAuthnStarted.current = false;
    }
  }

  function retryWebAuthn() {
    webAuthnStarted.current = false;
    startWebAuthnRegistration();
  }

  // ── TOTP generation + confirmation ────────────────────────────────────────

  async function generateTotp() {
    setLoading(true);
    setError('');
    try {
      const result = await setup('totp-generate', { challengeToken });
      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }
      setSetupToken(result.setupToken);
      setOtpauthUri(result.otpauthUri);
      setTotpLabel(result.totpLabel || null);
      setTotpTag(result.totpTag || null);
      setStep('totp');
      setLoading(false);
    } catch {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  }

  async function confirmTotp() {
    const code = totpCode.replace(/\s/g, '');
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code shown in your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await setup('totp-confirm', { setupToken, code });
      if (result.error) {
        setError(result.error || 'Invalid code. Try again.');
        setLoading(false);
        return;
      }
      onComplete(result.token, result.user);
    } catch {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  }

  // ── Step: choose ─────────────────────────────────────────────────────────

  if (step === 'choose') {
    return (
      <>
        <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
          Choose your authenticator
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20, lineHeight: 1.5 }}>
          Pick the method you want to use for future sign-ins.
        </div>

        {/* TOTP option */}
        <button
          onClick={generateTotp}
          disabled={loading}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            textAlign: 'left',
            padding: '14px 16px',
            marginBottom: 20,
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            cursor: loading ? 'wait' : 'pointer',
            color: 'var(--fg)',
            fontFamily: 'var(--font-body)',
            transition: 'border-color 0.15s',
            opacity: loading ? 0.7 : 1,
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.borderColor = 'var(--accent)';
          }}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
        >
          <span style={{ fontSize: 22, lineHeight: 1, marginTop: 1 }}>📱</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>Authenticator app</div>
            <div style={{ fontSize: 11, color: 'var(--fg3)', lineHeight: 1.5 }}>
              6-digit codes from Microsoft Authenticator, Google Authenticator, Apple Passwords,
              Authy, or any TOTP-compatible app.
            </div>
          </div>
        </button>

        {error && <ErrorBanner msg={error} />}

        <button style={backBtn} onClick={onBack}>
          ← Back
        </button>
      </>
    );
  }

  // ── Step: webauthn (registration in progress) ─────────────────────────────

  if (step === 'webauthn') {
    return (
      <>
        <div style={{ marginBottom: 20, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
          Register your passkey
        </div>

        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              borderRadius: '50%',
              border: '1px solid var(--border)',
              background: 'var(--bg3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
            }}
          >
            🔑
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg2)', lineHeight: 1.7 }}>
            {loading ? 'Follow the prompt on your device…' : 'Ready to register.'}
          </div>
          {loading && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg3)',
                fontFamily: 'var(--font-mono)',
                marginTop: 8,
              }}
            >
              Your browser will ask you to confirm with biometrics or a PIN
            </div>
          )}
        </div>

        <ErrorBanner msg={error} />

        {!loading && error && (
          <>
            <button
              className="btn btn-primary"
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: '11px 0',
                fontSize: 13,
                marginBottom: 8,
              }}
              onClick={retryWebAuthn}
            >
              Try again
            </button>
            <button
              onClick={() => {
                setStep('choose');
                setError('');
              }}
              style={{
                width: '100%',
                padding: '10px 0',
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--fg2)',
                fontFamily: 'var(--font-body)',
              }}
            >
              Use authenticator app instead
            </button>
          </>
        )}

        {!loading && !error && (
          <button
            style={backBtn}
            onClick={() => {
              setStep('choose');
              setError('');
            }}
          >
            ← Choose a different method
          </button>
        )}
      </>
    );
  }

  // ── Step: totp (QR scan + code confirm) ──────────────────────────────────

  if (step === 'totp') {
    return (
      <>
        <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
          Set up authenticator app
        </div>

        {/* Step 1: Scan */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 8,
            }}
          >
            1 — Scan this QR code
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 12 }}>
            Open Microsoft Authenticator, Google Authenticator, Apple Passwords, or any TOTP app and
            scan this code.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                background: 'white',
                padding: 12,
                borderRadius: 10,
                display: 'inline-block',
              }}
            >
              {otpauthUri && <QRCode value={otpauthUri} size={168} level="M" />}
            </div>
          </div>

          {/* Name the entry this QR creates. Older entries from a previous setup
              look identical and never work — the tag is how the user tells them
              apart, here and on the sign-in screen. */}
          {totpTag && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.6 }}>
                In your app this appears as{' '}
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg)' }}>
                  Tryggr — {totpLabel}
                </span>
                .
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.6, marginTop: 6 }}>
                If you already have other Tryggr entries, delete them — only the one ending{' '}
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg)' }}>
                  {totpTag}
                </span>{' '}
                will work from now on.
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Confirm */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 8,
            }}
          >
            2 — Enter the 6-digit code
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg2)', lineHeight: 1.5, marginBottom: 12 }}>
            Your app will show a code immediately after scanning. Enter it below to confirm the
            setup.
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={monoLabel}>Confirmation code</label>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && confirmTotp()}
              placeholder="000000"
              style={{
                letterSpacing: '0.3em',
                textAlign: 'center',
                fontSize: 22,
                fontFamily: 'var(--font-mono)',
              }}
              autoFocus
            />
          </div>

          <ErrorBanner msg={error} />

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
            onClick={confirmTotp}
            disabled={loading || totpCode.length !== 6}
          >
            {loading ? 'Confirming…' : 'Confirm and activate'}
          </button>
        </div>

        <button
          style={backBtn}
          onClick={() => {
            setStep('choose');
            setError('');
            setTotpCode('');
          }}
        >
          ← Choose a different method
        </button>
      </>
    );
  }

  return null;
}

export default MfaSetupModal;
